import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _extends from '@babel/runtime/helpers/esm/extends';
import React, { forwardRef } from 'react';
import ReactSelect, { components } from 'react-select';
import { Box, useTheme, Stack, omitThemingProps, useFormControlContext, useFormControl, Flex } from '@chakra-ui/react';
import { SearchIcon, CheckIcon } from '@outdoorly/icons';
import { Button } from '@outdoorly/button';
import { Text, getTextStyles } from '@outdoorly/text';
import { Divider } from '@outdoorly/divider';
import propTypes from 'prop-types';

var _excluded = ["showValues"];

var SelectIcon = function SelectIcon() {
  var theme = useTheme();
  return /*#__PURE__*/React.createElement(Box, {
    style: {
      position: 'absolute',
      display: 'inline-flex',
      width: '1.5rem',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      right: '0.5rem',
      pointerEvents: 'none',
      top: '50%',
      transform: 'translateY(-50%)'
    }
  }, /*#__PURE__*/React.createElement(CheckIcon, {
    role: "presentation",
    focusable: false,
    "aria-hidden": true // force icon to adhere to `IconWrapper` styles
    ,
    style: {
      width: '1em',
      height: '1em'
    },
    color: theme.colors.red['500'],
    strokeWidth: 3
  }));
};
/* eslint-disable react/prop-types */


var SingleValue = function SingleValue(props) {
  var size = props.size,
      theme = props.theme,
      children = props.children,
      color = props.color;
  return /*#__PURE__*/React.createElement(Text, {
    size: size,
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    maxWidth: "calc(100% - 8px)",
    mr: 1,
    css: {
      ':last-of-type': {
        marginRight: 0
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      color: color || theme.colors.neutral90,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  }, children));
};
/* eslint-enable react/prop-types */

/* eslint-disable react/prop-types */


var Placeholder = function Placeholder(props) {
  return /*#__PURE__*/React.createElement(SingleValue, _extends({}, props, {
    color: props.theme.colors.neutral50
  }));
};
/* eslint-enable react/prop-types */

/* eslint-disable react/prop-types */


var Option = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    ref: ref
  }, /*#__PURE__*/React.createElement(components.Option, props), props.isSelected && /*#__PURE__*/React.createElement(SelectIcon, null));
}); // eslint-disable-next-line react/display-name

var ProductAttributeOption = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    ref: ref
  }, /*#__PURE__*/React.createElement(components.Option, props), props.isSelected && !props.data.isSoldOut && !props.data.isUnavailable && /*#__PURE__*/React.createElement(SelectIcon, null), props.data.isSoldOut && /*#__PURE__*/React.createElement(Text, {
    position: "absolute",
    right: 4,
    top: 4,
    color: "scheme.controlInactive"
  }, "Out of Stock"), props.data.isUnavailable && !props.data.isSoldOut && /*#__PURE__*/React.createElement(Text, {
    position: "absolute",
    right: 4,
    top: 4,
    color: "scheme.controlInactive"
  }, "Unavailable"));
});
/* eslint-enable react/prop-types */

if (process.env.NODE_ENV !== 'production') {
  Option.displayName = 'Option';
}
/* eslint-disable react/prop-types */


var MenuList = /*#__PURE__*/forwardRef(function (props, ref) {
  var theme = useTheme();
  return /*#__PURE__*/React.createElement(Stack, _extends({
    ref: ref,
    spacing: theme.space[1],
    as: components.MenuList
  }, props));
});
/* eslint-enable react/prop-types */

if (process.env.NODE_ENV !== 'production') {
  MenuList.displayName = 'MenuList';
}

var composeStyles = function composeStyles() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function (provided, state) {
    return fns.filter(Boolean).reduce(function (styles, fn) {
      return fn(styles, state);
    }, provided);
  };
};

var getReactSelectTheme = function getReactSelectTheme(_ref) {
  var reactSelectTheme = _ref.reactSelectTheme,
      theme = _ref.theme;
  return _objectSpread(_objectSpread({}, reactSelectTheme), {}, {
    // For a list of what these represent, see:
    // https://github.com/JedWatson/react-select/issues/3692#issuecomment-523425096
    colors: _objectSpread(_objectSpread({}, reactSelectTheme.colors), {}, {
      /*
       * control/boxShadow(focused)
       * control/borderColor(focused)
       * control/borderColor:hover(focused)
       * option/backgroundColor(selected)
       * option/backgroundColor:active(selected)
       */
      primary: theme.colors.blue['400'],

      /*
       * option/backgroundColor:active
       */
      primary50: theme.colors.red['200'],

      /*
       * option/backgroundColor:hover/:focus
       */
      primary25: theme.colors.red['100'],

      /*
       * control/backgroundColor
       * menu/backgroundColor
       * option/color(selected)
       */
      neutral0: theme.colors.white,

      /*
       * control/backgroundColor(disabled)
       */
      neutral5: theme.colors.gray['100'],

      /*
       * control/borderColor(disabled)
       * multiValue/backgroundColor
       * indicators(separator)/backgroundColor(disabled)
       */
      neutral10: theme.colors.gray['200'],

      /*
       * control/borderColor
       * option/color(disabled)
       * indicators/color
       * indicators(separator)/backgroundColor
       * indicators(loading)/color
       */
      neutral20: theme.colors.gray['300'],

      /*
       * control/borderColor(focused)
       * control/borderColor:hover
       */
      neutral30: theme.colors.gray['400'],

      /*
       * menu(notice)/color
       * singleValue/color(disabled)
       * indicators/color:hover
       */
      neutral40: theme.colors.gray['500'],

      /*
       * placeholder/color
       */
      neutral50: theme.colors.gray['600'],

      /*
       * indicators/color(focused)
       * indicators(loading)/color(focused)
       */
      neutral60: theme.colors.gray['700'],
      neutral70: theme.colors.gray['800'],

      /*
       * input/color
       * multiValue(label)/color
       * singleValue/color
       * indicators/color(focused)
       * indicators/color:hover(focused)
       */
      neutral80: theme.colors.gray['900'],

      /*
       * multiValue(remove)/color:hover
       */
      danger: theme.colors.scheme.controlError,

      /*
       * multiValue(remove)/backgroundColor(focused)
       * multiValue(remove)/backgroundColor:hover
       */
      dangerLight: theme.colors.scheme.controlErrorBg
    })
  });
};

var getReactSelectStyles = function getReactSelectStyles(_ref2) {
  var styles = _ref2.styles,
      field = _ref2.field,
      textStyles = _ref2.textStyles,
      theme = _ref2.theme;
  return _objectSpread(_objectSpread({}, styles), {}, {
    option: composeStyles(function (provided, state) {
      var soldOutStyles = state.data.isSoldOut || state.data.isUnavailable ? {
        backgroundColor: "gray.50",
        color: "scheme.controlInactive"
      } : {};
      return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, provided), textStyles), state.isFocused ? {
        backgroundColor: state.theme.colors.primary25,
        color: state.theme.colors.neutral80
      } : state.isSelected && {
        backgroundColor: state.theme.colors.neutral0,
        color: state.theme.colors.neutral80
      }), {}, {
        ':active': {
          backgroundColor: !state.isDisabled && state.theme.colors.primary50
        }
      }, soldOutStyles), {}, {
        // This can trigger when used within the popup below. Since we are
        // already styling the focused state with background colours, it's
        // safe to remove this.
        ':focus': {
          outline: 'none'
        },
        cursor: 'pointer',
        paddingTop: '0.875rem',
        paddingBottom: '0.875rem',
        paddingRight: '2rem',
        paddingLeft: '0.875rem',
        borderRadius: theme.radii.lg
      });
    }, styles === null || styles === void 0 ? void 0 : styles.option),
    menu: composeStyles(function (provided) {
      return _objectSpread(_objectSpread({}, provided), {}, {
        boxShadow: theme.shadows.tooltip,
        borderRadius: theme.radii.lg
      });
    }, styles === null || styles === void 0 ? void 0 : styles.menu),
    menuList: composeStyles(function (provided) {
      return _objectSpread(_objectSpread({}, provided), {}, {
        padding: theme.space[2]
      });
    }, styles === null || styles === void 0 ? void 0 : styles.menuList),
    control: composeStyles(function (provided) {
      return _objectSpread(_objectSpread({}, provided), {}, {
        borderRadius: theme.radii.lg
      }, (field === null || field === void 0 ? void 0 : field.isInvalid) && {
        borderColor: theme.colors.scheme.controlError,
        backgroundColor: theme.colors.scheme.controlErrorBg,
        ':hover': {
          borderColor: theme.colors.scheme.controlError
        }
      });
    }, styles === null || styles === void 0 ? void 0 : styles.control),
    dropdownIndicator: composeStyles(function (provided) {
      return _objectSpread(_objectSpread({}, provided), (field === null || field === void 0 ? void 0 : field.isInvalid) && {
        color: theme.colors.scheme.controlError,
        ':hover': {
          color: theme.colors.scheme.controlErrorBg
        }
      });
    }, styles === null || styles === void 0 ? void 0 : styles.dropdownIndicator),
    valueContainer: composeStyles(function (provided) {
      return _objectSpread(_objectSpread({}, provided), {}, {
        paddingLeft: theme.space[3],
        paddingRight: 0
      });
    }, styles === null || styles === void 0 ? void 0 : styles.valueContainer),
    input: composeStyles(function (provided) {
      return _objectSpread(_objectSpread({}, provided), textStyles);
    }, styles === null || styles === void 0 ? void 0 : styles.input),
    placeholder: composeStyles(function (provided) {
      return _objectSpread(_objectSpread({}, provided), textStyles);
    }, styles === null || styles === void 0 ? void 0 : styles.placeholder),
    container: function container() {
      return {
        width: '100%'
      };
    }
  });
};
/**
 * A styled wrapper over [`react-select`](https://react-select.com/). All of
 * [`react-select`'s props](https://react-select.com/props) are available.
 */


var Select = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var _props$size;

  var _ref3$showValues = _ref3.showValues,
      showValues = _ref3$showValues === void 0 ? true : _ref3$showValues,
      props = _objectWithoutProperties(_ref3, _excluded);

  var _theme = useTheme();

  var textStyles = getTextStyles({
    theme: _theme,
    size: (_props$size = props.size) !== null && _props$size !== void 0 ? _props$size : 'lg'
  });
  var ownProps = omitThemingProps(props);
  var field = useFormControlContext();
  var input = useFormControl(ownProps);
  input.isDisabled = input.disabled;
  delete input.disabled;
  return /*#__PURE__*/React.createElement(ReactSelect, _extends({
    innerRef: ref
  }, input, {
    hideSelectedOptions: false,
    tabSelectsValue: false,
    controlShouldRenderValue: showValues // Use theme to overwrite most colors
    ,
    theme: function theme(reactSelectTheme) {
      return getReactSelectTheme({
        reactSelectTheme: reactSelectTheme,
        theme: _theme
      });
    } // use styles to overwrite some specific colors
    ,
    styles: getReactSelectStyles({
      styles: props.styles,
      field: field,
      textStyles: textStyles,
      theme: _theme
    }),
    isSearchable: props.isSearchable,
    onChange: function onChange(e) {
      return props.onChange && props.onChange(e);
    },
    onMenuOpen: function onMenuOpen() {
      return props.onMenuOpen && props.onMenuOpen(input);
    } // finally, style the component directly by customizing it
    ,
    components: _objectSpread({
      Option: Option,
      IndicatorSeparator: function IndicatorSeparator() {
        return null;
      },
      Placeholder: Placeholder,
      SingleValue: SingleValue,
      MenuList: MenuList
    }, props.components)
  }));
});
var ProductAttributeSelect = function ProductAttributeSelect(props) {
  return /*#__PURE__*/React.createElement(Select, _extends({}, props, {
    onMenuOpen: function onMenuOpen(input) {
      return props.setAttributeToIgnore(input);
    },
    onChange: function onChange(e) {
      if (props.onChange) props.onChange(e);
      if (props.resetOthers && e !== null && e !== void 0 && e.isUnavailable) props.resetOthers(props.name);
    },
    components: {
      Option: ProductAttributeOption
    }
  }));
};

if (process.env.NODE_ENV !== 'production') {
  Select.displayName = 'Select';
}

Select.propTypes = {
  size: Text.propTypes.size,

  /**
   * See [`react-select` styles](https://react-select.com/styles)
   */
  styles: propTypes.object,

  /**
   * See [`react-select` components](https://react-select.com/components)
   */
  components: propTypes.object,

  /**
   * if false, removes ability to search dropdown
   */
  isSearchable: propTypes.bool,

  /**
   * Display or hide the selected values within the input component
   */
  showValues: propTypes.bool
};
var DropdownIndicator = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(components.DropdownIndicator, _extends({
    ref: ref
  }, props), /*#__PURE__*/React.createElement(SearchIcon, null));
});

if (process.env.NODE_ENV !== 'production') {
  DropdownIndicator.displayName = 'DropdownIndicator';
}

var Menu = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 1,
    ref: ref
  }, /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(components.Menu, props));
});

if (process.env.NODE_ENV !== 'production') {
  Menu.displayName = 'Menu';
}
/* eslint-disable react/prop-types */


var SelectContainer = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 4,
    ref: ref
  }, /*#__PURE__*/React.createElement(Flex, {
    pl: 3,
    justify: "space-between"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "sm",
    color: "scheme.textSecondary"
  }, props.getValue().length || 'Nothing', " Selected"), props.hasValue && /*#__PURE__*/React.createElement(Button, {
    variant: "link",
    colorScheme: "gray",
    color: "scheme.textSecondary",
    onClick: props.clearValue,
    fontSize: "sm"
  }, "Clear")), /*#__PURE__*/React.createElement(components.SelectContainer, props));
});
/* eslint-enable react/prop-types */

if (process.env.NODE_ENV !== 'production') {
  SelectContainer.displayName = 'SelectContainer';
}

var MultiSelect = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Select, _extends({
    ref: ref
  }, props, {
    isMulti: true,
    isClearable: false,
    components: _objectSpread(_objectSpread({}, props.components), {}, {
      SelectContainer: SelectContainer
    })
  }));
});

if (process.env.NODE_ENV !== 'production') {
  MultiSelect.displayName = 'MultiSelect';
}

MultiSelect.propTypes = _objectSpread({}, Select.propTypes);

export { MultiSelect, ProductAttributeSelect, Select };
